import React, { useState, useEffect, useContext } from "react";
import { Card, FormLayout, TextField, Select, Text, Stack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../components/global/appContext";
import useLanguageSelector from "../../../hooks/useLanguageSelector";
import LanguageSelector from "../../../components/components/LanguageSelector";

export default function SEOEditor(props) {
  const { t } = useTranslation();
  const { languageTranslateIconUrl } = useContext(AppContext);
  const [editingMeta, setEditingMeta] = useState(false);
  const { area, handleUpdate, newLocation: newPlace } = props;
  
  // Use the new language selector hook
  const {
    currentLocale,
    isLocalizationEnabled,
    localeOptions,
    languageSettings
  } = useLanguageSelector(area);
  
  // Add defensive check for area
  if (!area) {
    return (
      <Card>
        <Card.Section title={t("seo.title")}>
          <Text variant="bodyMd" color="subdued">{t("seo.loading")}</Text>
        </Card.Section>
      </Card>
    );
  }
  
  const { seo_template, preview_url_prefix } = area;
  
  const [selectedLocale, setSelectedLocale] = useState(currentLocale);
  
  useEffect(() => {
    if (currentLocale && selectedLocale !== currentLocale) {
      setSelectedLocale(currentLocale);
    }
  }, [currentLocale]);
  
  const isDefaultLocale = selectedLocale === currentLocale;

  // Get field value for current locale
  const getFieldValue = (fieldName) => {
    if (!area) return "";
    
    if (selectedLocale === currentLocale) {
      return area[fieldName] || "";
    } else {
      // Check both sources: location_translations (from server) and translations (being edited)
      const savedTranslation = area.location_translations?.[fieldName]?.[selectedLocale];
      const editingTranslation = area.translations?.[fieldName]?.[selectedLocale];
      
      // Prefer the editing translation if it exists, otherwise use saved translation
      return editingTranslation !== undefined ? editingTranslation : (savedTranslation || "");
    }
  };

  // Get placeholder text for non-default locales
  const getPlaceholderText = (fieldName) => {
    if (!area) return "";
    
    if (selectedLocale === currentLocale) {
      return !newPlace && seo_template?.[fieldName]?.placeholder;
    } else {
      const defaultContent = area[fieldName] || "";
      return defaultContent ? t("common.default", { value: defaultContent }) : "";
    }
  };

  // Handle field updates with translation support
  const handleFieldUpdate = (fieldName, value) => {
    if (!area) return;
    
    if (selectedLocale === currentLocale) {
      // Default locale - update main field
      handleUpdate(fieldName, value);
    } else {
      // Non-default locale - update translations (what backend expects)
      const newTranslations = area.translations ? { ...area.translations } : {};
      if (!newTranslations[fieldName]) {
        newTranslations[fieldName] = {};
      }
      newTranslations[fieldName][selectedLocale] = value;
      handleUpdate("translations", newTranslations);
    }
  };

  const textFieldProps = (key) => ({
    value: getFieldValue(key),
    onChange: (value) => handleFieldUpdate(key, value),
    error: area?.errors[key]?.join(", "),
    clearButton: true,
    onClearButtonClick: () => handleFieldUpdate(key, ""),
    placeholder: getPlaceholderText(key),
  });

  // Language selector component for the card header
  const languageSelector = (
    <LanguageSelector
      options={localeOptions}
      value={selectedLocale}
      onChange={setSelectedLocale}
    />
  );

  return (
    <Card>
      <Card.Section
        title={
          <Stack distribution="equalSpacing" alignment="center">
            <Text variant="headingMd" as="h2">{t("seo.title")}</Text>
            {languageSelector}
          </Stack>
        }
        actions={seo_template ? [
          {
            content: t("seo.editTemplate"),
            url: `/seo-templates/${seo_template.type}`,
          },
        ] : []}
      >
        {isLocalizationEnabled && !isDefaultLocale && (
          <div style={{ marginBottom: '1rem' }}>
            <Text variant="bodyMd" color="subdued">
              {t("seo.editingLocaleHelp", {
                locale: languageSettings?.available_locales?.find(l => l.code === selectedLocale)?.name || selectedLocale.toUpperCase(),
                defaultLocale: languageSettings?.available_locales?.find(l => l.code === currentLocale)?.name || currentLocale.toUpperCase()
              })}
            </Text>
          </div>
        )}
        <FormLayout>
          <TextField label={t("seo.pageTitle")} {...textFieldProps("page_title")} />
          <TextField
            label={t("seo.pageDescription")}
            multiline={4}
            {...textFieldProps("page_description")}
          />
        </FormLayout>
      </Card.Section>

      {editingMeta || props.newLocation ? (
        <Card.Section
          title={t("seo.searchEnginePreview")}
          actions={
            !newPlace && [
              {
                content: t("seo.previewMeta"),
                onAction: () => setEditingMeta(false),
              },
            ]
          }
        >
          <FormLayout>
            <TextField label={t("seo.metaTitle")} {...textFieldProps("meta_title")} />
            <TextField
              label={t("seo.metaDescription")}
              multiline={4}
              maxLength={320}
              {...textFieldProps("meta_description")}
            />
            {!newPlace && (
              <TextField
                label={t("seo.urlHandle")}
                prefix={`${preview_url_prefix}/`}
                value={area.slug || ""}
                onChange={(value) => handleUpdate("slug", value)}
                error={area?.errors?.slug?.join(", ")}
                clearButton={false}
              />
            )}
          </FormLayout>
        </Card.Section>
      ) : (
        <Card.Section
          title={t("seo.searchEnginePreview")}
          actions={[
            { content: t("seo.editMeta"), onAction: () => setEditingMeta(true) },
          ]}
        >
          <div className="seo-preview">
            <p className="page-title">
              {getFieldValue("meta_title") || getFieldValue("page_title") || area.name}
            </p>
            <p className="preview-url">
              {`${preview_url_prefix}/${area.slug}`}
            </p>
            <p className="page-description">
              {getFieldValue("meta_description") || getFieldValue("page_description")}
            </p>
          </div>
        </Card.Section>
      )}
    </Card>
  );
}
